import cn from 'classnames';
import moment from 'moment';
import React, {useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {RuntimeSummary} from '../RuntimeSummary';
import {getProjectData} from '../../../store/project';
import {Project} from '../../../types/project/Project';
import {toast} from '../../../base-components/StudioToast';
import {setONNXTask, TASK_STATUS, useDeploy} from '../useDeploy';
import {Parameter} from '../../../types/model/framework/Parameter';
import {ONNXRuntimeContainer} from '../../ONNXRuntimeContainer/ONNXRuntimeContainer';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';

import './TestResults.scss';
import {VideoStreamSubject} from '../HlsVideoStream';
import {WebCameraTestInferenceResults} from '../TestInferenceResults/WebCameraTestInferenceResults';
import {HlsContainer, StreamListener} from './HlsContainer';

export type TestResultsProps = {
  deployment: DeploymentDetailsInfo | null;
  streamSubject: VideoStreamSubject;
  isDeploying: boolean;
};

export const TestResults = ({
  deployment,
  streamSubject,
  isDeploying,
}: TestResultsProps) => {
  const [
    {deployConfig, deployTask, streamingTask, onnxTask, streamingDevices},
    dispatch,
  ] = useDeploy();

  const previousOnnxResults = useRef<JSX.Element>();

  const projectData = useSelector(getProjectData);

  const runtimeSummaryRef = useRef(null) as any;

  const deploymentApplication = deployment?.applicationId
    ? findApplication(projectData, deployment?.applicationId)
    : null;
  const taskStatistics = deployTask?.monitoringResponse?.statistics;
  const deploymentStatistics = deployment?.deploymentStatistics;
  const [isStreamingInference, setStreamingInference] = useState(false);
  const isWebCameraInference = streamingTask.status !== TASK_STATUS.IDLE;
  const isONNXInference = onnxTask.status !== TASK_STATUS.IDLE;
  const isONNXStreamingFinished =
    onnxTask.params?.inferenceSource !== 'IMAGES' &&
    onnxTask.status === TASK_STATUS.COMPLETE;
  const inProgressListener: StreamListener = {
    setInProgress: value => setStreamingInference(value),
    isInProgress: () => isStreamingInference,
  };

  const renderPreviousResultsOrNewResults = () => {
    const onnxRuntimeContainer = renderONNXRuntimeContainer();
    if (onnxRuntimeContainer) {
      previousOnnxResults.current = onnxRuntimeContainer;
    }
    return previousOnnxResults.current;
  };

  const renderONNXRuntimeContainer = () => {
    const {status, params, id} = onnxTask;
    const isONNXInference = status !== TASK_STATUS.IDLE;
    if (isONNXInference && params) {
      const selectedApplication = findApplication(projectData, params.deploymentId);
      const onnxVideoURL =
        params.inferenceSource === 'IP_CAMERA'
          ? streamingDevices.find(camera => camera.id === params.ipCamera)?.url
          : params.inferenceSource === 'FILE'
          ? streamingDevices.find(file => file.id === params.videoFile)?.url
          : undefined;

      const configuredParameters = (
        deployConfig.postProcessorParams?.map<Parameter>(param => ({
          id: param.id,
          name: param.name,
          value: param.value,
        })) || []
      )
        .concat(deployConfig.postProcessorRealtimeParams || [])
        .concat(
          deployConfig.preProcessorParams?.map<Parameter>(param => ({
            id: param.id,
            name: param.name,
            value: param.value,
          })) || []
        )
        .concat(deployConfig.preProcessorRealtimeParams || []);

      return selectedApplication &&
        !isONNXStreamingFinished &&
        status === TASK_STATUS.IN_PROGRESS ? (
        <ONNXRuntimeContainer
          key={id}
          projectId={projectData.id}
          application={selectedApplication}
          files={params.files.map(f => f.file)}
          inProgress={onnxTask?.status === 'inProgress'}
          runtimeMode={params.runtimeMode}
          type={params.inferenceSource === '' ? undefined : params.inferenceSource}
          url={onnxVideoURL}
          configuredParams={configuredParameters}
          displayRealtimeParameters={true}
          onError={message => {
            if (message) {
              toast.error(message);
            }
            dispatch(setONNXTask({status: TASK_STATUS.IDLE}));
          }}
          onFinish={() => dispatch(setONNXTask({status: TASK_STATUS.COMPLETE}))}
        />
      ) : null;
    }
    return null;
  };

  const isWebCamDeployment = useMemo(
    () => deployConfig.inferenceSource === 'WEB_CAMERA',
    [deployConfig.inferenceSource]
  );

  const trainImagesExist = Boolean(
    deploymentApplication?.dataSet?.labelDistribution?.TRAIN?.imagesCount
  );
  const testImagesExist = Boolean(
    deploymentApplication?.dataSet?.labelDistribution?.TEST?.imagesCount
  );
  const validationImagesExist = Boolean(
    deploymentApplication?.dataSet?.labelDistribution?.VALIDATION?.imagesCount
  );
  const datasetExists = trainImagesExist || testImagesExist || validationImagesExist;
  const datasetSourceSelected =
    deployConfig.testSource === 'TRAIN' ||
    deployConfig.testSource === 'VALIDATION' ||
    deployConfig.testSource === 'TEST';

  const accuracy =
    taskStatistics?.inferenceAccuracy ??
    deploymentStatistics?.lastTop1Accuracy ??
    deploymentStatistics?.accuracy ??
    null;

  // don't show accuracy when modelStatistics is not in outputTypes
  const noModelStatistics =
    datasetExists &&
    datasetSourceSelected &&
    (accuracy === 0 || accuracy === null) &&
    !deployConfig.outputTypes.includes('modelStatistics');

  return (
    <div className="test-results">
      {!isONNXInference && (
        <>
          {(isDeploying || deployment) && (
            <RuntimeSummary
              name={deployTask?.monitoringResponse?.name ?? deployment?.nodeName ?? null}
              status={deployTask?.monitoringResponse?.statusMessage ?? null}
              fps={
                taskStatistics?.outputVideoFps ??
                deploymentStatistics?.outputVideoFps ??
                taskStatistics?.inferenceFPS ??
                deploymentStatistics?.fps ??
                null
              }
              latency={
                taskStatistics?.inferenceLatency ?? deploymentStatistics?.latency ?? null
              }
              accuracy={
                noModelStatistics
                  ? null
                  : taskStatistics?.inferenceAccuracy ??
                    deploymentStatistics?.lastTop1Accuracy ??
                    deploymentStatistics?.accuracy ??
                    null
              }
              runtime={
                !isDeploying && deployment
                  ? moment(deployment?.endTime).diff(
                      deployment?.startTime,
                      'milliseconds'
                    ) / 1000
                  : null
              }
              componentRef={runtimeSummaryRef}
            />
          )}
          <div
            className={cn(
              'test-results__split-inference-results',
              (isStreamingInference || isWebCameraInference) &&
                'test-results__split-inference-results--streaming'
            )}
          >
            {isWebCamDeployment && (
              <WebCameraTestInferenceResults
                deployment={deployment}
                hasTransformation={Boolean(deploymentApplication?.transformation)}
                isStreamingInference={isWebCameraInference}
                isOtherTaskInProgress={isDeploying}
                projectData={projectData}
                displayRealtimeParameters={true}
              />
            )}
            {!isWebCamDeployment && (
              <HlsContainer
                deployment={deployment}
                inputSourceType={deployConfig.inferenceSource}
                streamSubject={streamSubject}
                deploymentApplication={deploymentApplication}
                isDeploying={isDeploying}
                projectData={projectData}
                inProgressListener={inProgressListener}
              />
            )}
          </div>
        </>
      )}
      {renderPreviousResultsOrNewResults()}
    </div>
  );
};

function findApplication(projectData: Project, applicationId: string) {
  const applications = Object.values(projectData?.applications || []);
  return applications.find(application => application.id === applicationId);
}
